import {
  DEFAULT_CROP_RECT,
  DEFAULT_IMAGE_FRAME,
  clampCropInsideImage,
  clampImageToCoverCrop,
  fitCropRectToAspect,
  hostFramesToSourceRect,
  isFullCropRect,
  normalizeCropRect,
  normalizeHostCropFrame,
  normalizeImageFrame,
  pixelAspectToNormalized,
  sourceRectToHostCropFrame,
} from "../../../components/shared/imageToolbar/imageCropModel";

export const CROP_OUTPUT_FORMATS = [
  { id: "jpg", label: "JPG" },
  { id: "png", label: "PNG" },
  { id: "webp", label: "WebP" },
];

export const CROP_PREP_PRESETS = [
  { id: "1:1", label: "1:1", ratio: 1 },
  { id: "16:9", label: "16:9", ratio: 16 / 9 },
  { id: "4:3", label: "4:3", ratio: 4 / 3 },
  { id: "3:4", label: "3:4", ratio: 3 / 4 },
  { id: "3:2", label: "3:2", ratio: 3 / 2 },
  { id: "2:3", label: "2:3", ratio: 2 / 3 },
  { id: "9:16", label: "9:16", ratio: 9 / 16 },
  { id: "freeform", label: "Freeform", ratio: null },
  { id: "custom", label: "Custom", ratio: "custom" },
];

export const CROP_ZOOM_MIN = 100;
export const CROP_ZOOM_MAX = 300;
export const CROP_ZOOM_DEFAULT = 100;

export function createDefaultCropPrepSettings() {
  return {
    preset: "freeform",
    customRatioW: 1,
    customRatioH: 1,
    zoom: CROP_ZOOM_DEFAULT,
    outputFormat: "png",
    imageFrame: { ...DEFAULT_IMAGE_FRAME },
    cropFrame: { ...DEFAULT_CROP_RECT },
  };
}

export function clampCropZoom(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return CROP_ZOOM_DEFAULT;
  return Math.max(CROP_ZOOM_MIN, Math.min(CROP_ZOOM_MAX, Math.round(n)));
}

export function clampCustomRatioPart(value, fallback = 1) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.max(1, Math.min(999, Math.round(n)));
}

/** Locked w/h aspect for crop handles, or null for freeform. */
export function resolveCropPrepAspectRatio(
  settings,
  naturalWidth = 0,
  naturalHeight = 0
) {
  const preset = settings?.preset || "freeform";
  const entry = CROP_PREP_PRESETS.find((p) => p.id === preset);
  if (!entry || entry.ratio == null) return null;
  if (entry.ratio === "custom") {
    const w = clampCustomRatioPart(settings?.customRatioW, 1);
    const h = clampCustomRatioPart(settings?.customRatioH, 1);
    return w / h;
  }
  return pixelAspectToNormalized(entry.ratio, naturalWidth, naturalHeight);
}

export function fitCropFrameForSettings(
  settings,
  naturalWidth = 0,
  naturalHeight = 0,
  fromFrame = null
) {
  const imageFrame = settings?.imageFrame || DEFAULT_IMAGE_FRAME;
  const aspect = resolveCropPrepAspectRatio(settings, naturalWidth, naturalHeight);
  const crop = !aspect
    ? normalizeHostCropFrame(fromFrame || DEFAULT_CROP_RECT)
    : fitCropRectToAspect(aspect, fromFrame || DEFAULT_CROP_RECT);
  return clampCropInsideVisibleImage(crop, imageFrame);
}

/** The original image always fits this host viewport at 100% zoom. */
export const CROP_PREP_HOST_BOUNDS = { x: 0, y: 0, w: 1, h: 1 };

/** Keep crop window inside the host viewport (original image edges at fit). */
export function clampCropInsideHostViewport(cropFrame) {
  let { x, y, w, h } = normalizeHostCropFrame(cropFrame);
  w = Math.min(w, CROP_PREP_HOST_BOUNDS.w);
  h = Math.min(h, CROP_PREP_HOST_BOUNDS.h);
  x = Math.max(
    CROP_PREP_HOST_BOUNDS.x,
    Math.min(CROP_PREP_HOST_BOUNDS.x + CROP_PREP_HOST_BOUNDS.w - w, x)
  );
  y = Math.max(
    CROP_PREP_HOST_BOUNDS.y,
    Math.min(CROP_PREP_HOST_BOUNDS.y + CROP_PREP_HOST_BOUNDS.h - h, y)
  );
  return normalizeHostCropFrame({ x, y, w, h });
}

/** Crop must stay inside both the image frame and the original host viewport. */
export function clampCropInsideVisibleImage(cropFrame, imageFrame) {
  const img = normalizeImageFrame(imageFrame);
  let crop = clampCropInsideImage(cropFrame, img);
  crop = clampCropInsideHostViewport(crop);
  return clampCropInsideImage(crop, img);
}

/** Keep zoomed image covering the host viewport so no empty margin appears. */
export function clampImageToHostBounds(imageFrame) {
  let { x, y, w, h } = normalizeImageFrame(imageFrame);
  if (w >= 1) {
    x = Math.min(0, Math.max(1 - w, x));
  }
  if (h >= 1) {
    y = Math.min(0, Math.max(1 - h, y));
  }
  return normalizeImageFrame({ x, y, w, h });
}

/** Image must cover the crop window and stay within original image bounds on the host. */
export function clampImageFrameForCropPrep(imageFrame, cropFrame) {
  const crop = normalizeHostCropFrame(cropFrame);
  let img = clampImageToHostBounds(
    clampImageToCoverCrop(normalizeImageFrame(imageFrame), crop)
  );
  // Never slide the image past the host edge and leave empty crop pixels.
  if (
    img.x > crop.x ||
    img.y > crop.y ||
    img.x + img.w < crop.x + crop.w ||
    img.y + img.h < crop.y + crop.h
  ) {
    img = clampImageToHostBounds(normalizeImageFrame(imageFrame));
  }
  return img;
}

/** Clamp crop + image together so the crop never leaves the source image. */
export function resolveCropPrepFrames(cropFrame, imageFrame) {
  let crop = clampCropInsideVisibleImage(cropFrame, imageFrame);
  let img = clampImageFrameForCropPrep(imageFrame, crop);
  crop = clampCropInsideVisibleImage(crop, img);
  img = clampImageFrameForCropPrep(img, crop);
  crop = clampCropInsideVisibleImage(crop, img);
  return { cropFrame: crop, imageFrame: img };
}

export function hasActiveCrop(settings) {
  return !isFullCropRect(getSourceCropRect(settings));
}

export function computeImageFrameForZoom(
  zoomPercent,
  cropFrame,
  prevImageFrame = DEFAULT_IMAGE_FRAME
) {
  const scale = clampCropZoom(zoomPercent) / 100;

  // At 100% the image always fits the canvas — discard pan from higher zoom levels.
  if (scale <= 1) {
    return { ...DEFAULT_IMAGE_FRAME };
  }

  const crop = normalizeHostCropFrame(cropFrame);
  const cx = crop.x + crop.w / 2;
  const cy = crop.y + crop.h / 2;
  const prev = normalizeImageFrame(prevImageFrame);
  const relX = prev.w > 0 ? (cx - prev.x) / prev.w : 0.5;
  const relY = prev.h > 0 ? (cy - prev.y) / prev.h : 0.5;
  // Larger image frame = zoomed-in image under the crop window.
  const w = scale;
  const h = scale;
  const x = cx - relX * w;
  const y = cy - relY * h;
  return clampImageFrameForCropPrep(
    normalizeImageFrame({ x, y, w, h }),
    crop
  );
}

export function getSourceCropRect(settings) {
  const resolved = resolveCropPrepFrames(
    settings?.cropFrame || DEFAULT_CROP_RECT,
    settings?.imageFrame || DEFAULT_IMAGE_FRAME
  );
  return hostFramesToSourceRect(resolved.cropFrame, resolved.imageFrame);
}

export function cropPrepToHostCropFrame(settings) {
  return normalizeHostCropFrame(settings?.cropFrame || DEFAULT_CROP_RECT);
}

export function cropOutputExtension(format = "jpg") {
  const id = String(format || "jpg").toLowerCase();
  if (id === "png") return "png";
  if (id === "webp") return "webp";
  return "jpg";
}

export function cropOutputMime(format = "jpg") {
  const id = String(format || "jpg").toLowerCase();
  if (id === "png") return "image/png";
  if (id === "webp") return "image/webp";
  return "image/jpeg";
}

export function sourceRectToHostFrames(sourceRect, imageFrame = DEFAULT_IMAGE_FRAME) {
  return {
    cropFrame: sourceRectToHostCropFrame(sourceRect, imageFrame),
    imageFrame: normalizeImageFrame(imageFrame),
  };
}
